import React, { useState, useEffect } from 'react'

import SvaypLeftMobilePhone from "../../../../assets/images/svayp/svayp_left.png"
import SvaypRightMobilePhone from "../../../../assets/images/svayp/svayp_right.png"

import SvaypLogo from "../../../../assets/images/svayp/svayp-logo.svg"

import AppStoreIcon from "../../../../assets/icons/app_store.svg"
import PlayStoreIcon from "../../../../assets/icons/play_store.svg"

import { motion, useAnimation } from 'framer-motion';


const Svayp = () => {
  const [logoHovered, setLogoHovered] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const controls = useAnimation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Trigger animation when component is in view
  useEffect(() => {
    controls.start({
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    });
  }, [controls]);

  // Generate random particles
  const particles = Array.from({ length: 15 }).map((_, i) => ({
    id: i,
    size: Math.random() * 3 + 1,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: Math.random() * 20 + 10,
    delay: Math.random() * 5
  }));

  return (
    <motion.div 
      className='mt-20 md:mt-36 relative opacity-90 px-4 md:px-0'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      {/* Background gradient */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] md:w-[500px] h-[300px] md:h-[500px] rounded-full bg-gradient-to-r from-purple-600/5 to-indigo-600/5 blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
          style={{
            transform: `translate(-50%, -50%) translateY(${scrollY * 0.05}px)`
          }}
        />
      </div>

      {/* Floating particles - fewer on mobile */}
      <div className="absolute inset-0 -z-5 overflow-hidden pointer-events-none">
        {particles.slice(0, window.innerWidth < 768 ? 8 : 15).map(particle => (
          <motion.div
            key={particle.id}
            className="absolute rounded-full"
            style={{
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              background: particle.id % 2 === 0 
                ? 'linear-gradient(to right, rgba(168, 85, 247, 0.4), rgba(139, 92, 246, 0.2))' 
                : 'linear-gradient(to right, rgba(139, 92, 246, 0.3), rgba(168, 85, 247, 0.1))',
              boxShadow: '0 0 4px rgba(139, 92, 246, 0.3)'
            }}
            animate={{
              x: [0, Math.random() * 50 - 25, 0],
              y: [0, Math.random() * 50 - 25, 0],
              opacity: [0, 0.7, 0],
              scale: [0, 1, 0]
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              delay: particle.delay,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      <div className='flex flex-col w-full items-center justify-center text-center'>
        {/* Logo with animation */}
        <motion.div
          className="relative"
          onMouseEnter={() => setLogoHovered(true)}
          onMouseLeave={() => setLogoHovered(false)}
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300, damping: 10 }}
        >
          <motion.img  
            initial={{ opacity: 0, translateY: "0px"}}  
            whileInView={{ opacity: 1, translateY: "24px" }} 
            transition={{duration: .7, delay: .5}} 
            className='w-[12rem] md:w-[16rem] drop-shadow-lg relative z-10' 
            src={SvaypLogo} 
            alt="Svayp Logo" 
            animate={{
              scale: logoHovered ? 1.05 : 1,
              transition: { duration: 0.3 }
            }}
          />
          <motion.div 
            className="absolute -inset-8 bg-gradient-to-r from-purple-600/10 to-indigo-600/10 rounded-full blur-xl -z-10"
            animate={{
              opacity: logoHovered ? 0.8 : 0,
              scale: logoHovered ? 1.1 : 1,
            }}
            transition={{ duration: 0.5 }}
          />
          
          {/* Orbit effect */}
          <motion.div 
            className="absolute -inset-12 -z-5 opacity-0"
            animate={{
              opacity: logoHovered ? 0.6 : 0,
            }}
            transition={{ duration: 0.3 }}
          >
            <motion.div 
              className="w-2 h-2 rounded-full bg-purple-500/50 absolute"
              style={{ top: '15%', left: '10%' }}
              animate={{ rotate: 360 }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: "linear",
                repeatType: "loop" 
              }}
            />
            <motion.div 
              className="w-1.5 h-1.5 rounded-full bg-indigo-500/50 absolute"
              style={{ bottom: '20%', right: '15%' }}
              animate={{ rotate: -360 }}
              transition={{ 
                duration: 4, 
                repeat: Infinity, 
                ease: "linear",
                repeatType: "loop" 
              }}
            />
          </motion.div>
        </motion.div>
        
        {/* Description text with animation */}
        <motion.div 
          initial={{ opacity: 0, translateY: "24px"}}  
          whileInView={{ opacity: 1, translateY: "0px" }} 
          transition={{duration: .7, delay: .5}} 
          className="mt-[50px] md:mt-[70px] text-sm md:text-base text-gray-200 select-none"
        >
          <motion.span 
            className="relative inline-block"
            whileHover={{ scale: 1.02 }}
          >
            Share your views on various topics with
            <motion.div 
              className="absolute -inset-1 -z-10 bg-gradient-to-r from-purple-600/5 to-indigo-600/5 rounded-lg blur-sm opacity-0 hover:opacity-100 transition-opacity duration-300"
            />
          </motion.span> <br />
          <motion.span 
            className="font-bold text-white inline-block relative"
            whileHover={{ 
              scale: 1.05,
              color: "#a78bfa",
              transition: { duration: 0.2 }
            }}
          >
            SVAYP - the ultimate public opinion platform
            <motion.div 
              className="absolute -bottom-1 left-0 right-0 h-[1px] bg-gradient-to-r from-purple-500/0 via-purple-500/50 to-purple-500/0"
              initial={{ scaleX: 0 }}
              whileHover={{ scaleX: 1 }}
              transition={{ duration: 0.3 }}
            />
          </motion.span>
          <br /> 
          Download our mobile app and get accurate insights while <br /> 
          joining the conversation with like-minded individuals.
        </motion.div>
        
        {/* App store buttons with animations */}
        <div className='flex md:flex-row flex-col gap-6 md:gap-24 mt-8 md:mt-12 items-center'>
          <a 
            href='https://apps.apple.com/us/app/svayp/id6447109874?l=us' 
            target='_blank' 
            rel="noreferrer"
            className="relative group"
          >
            <motion.div 
              className="absolute -inset-1 bg-gradient-to-r from-purple-600/20 to-indigo-600/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition duration-1000"
              animate={{
                rotate: [0, 5, 0, -5, 0],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
            <motion.img  
              initial={{ opacity: 0, translateX: "0px"}}  
              whileInView={{ 
                opacity: 1, 
                translateX: ["0px", window.innerWidth >= 768 ? "-12px" : "0px", window.innerWidth >= 768 ? "-12px" : "0px"] 
              }} 
              transition={{
                duration: .7, 
                delay: .7,
                times: [0, 0.8, 1],
                ease: "easeOut"
              }} 
              className="transition-all cursor-pointer w-32 md:w-40 hover:scale-110 duration-700 opacity-80 hover:opacity-100 relative" 
              src={AppStoreIcon} 
              alt="App Store"
              whileHover={{
                y: -5,
                filter: "drop-shadow(0 0 8px rgba(139, 92, 246, 0.5))",
                transition: { duration: 0.2 }
              }}
            />
            
            {/* Fixed shine effect */}
            <div className="absolute inset-0 overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg pointer-events-none">
              <motion.div 
                className="absolute h-full w-[100%] bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12"
                initial={{ left: "-100%" }}
                animate={{ left: "100%" }}
                transition={{ 
                  duration: 1.5, 
                  repeat: Infinity, 
                  repeatDelay: 2,
                  ease: "easeInOut" 
                }}
              />
            </div>
          </a>
          
          <a 
            href='https://play.google.com/store/apps/details?id=com.svayp.svayp' 
            target='_blank' 
            rel="noreferrer"
            className="relative group"
          >
            <motion.div 
              className="absolute -inset-1 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition duration-1000"
              animate={{
                rotate: [0, -5, 0, 5, 0],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
            <motion.img  
              initial={{ opacity: 0, translateX: "0px"}}  
              whileInView={{ 
                opacity: 1, 
                translateX: ["0px", window.innerWidth >= 768 ? "+12px" : "0px", window.innerWidth >= 768 ? "+12px" : "0px"] 
              }} 
              transition={{
                duration: .7, 
                delay: .7,
                times: [0, 0.8, 1],
                ease: "easeOut"
              }} 
              className="transition-all cursor-pointer w-32 md:w-40 hover:scale-110 duration-700 opacity-80 hover:opacity-100 relative" 
              src={PlayStoreIcon} 
              alt="Play Store"
              whileHover={{
                y: -5,
                filter: "drop-shadow(0 0 8px rgba(139, 92, 246, 0.5))",
                transition: { duration: 0.2 }
              }}
            />
            
            {/* Fixed shine effect */}
            <div className="absolute inset-0 overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg pointer-events-none">
              <motion.div 
                className="absolute h-full w-[100%] bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12"
                initial={{ left: "-100%" }}
                animate={{ left: "100%" }}
                transition={{ 
                  duration: 1.5, 
                  repeat: Infinity, 
                  repeatDelay: 2,
                  ease: "easeInOut",
                  delay: 0.3
                }}
              />
            </div>
          </a>
        </div>
        
        {/* Description of work with animation */}
        <motion.div 
          initial={{ opacity: 0, translateY: "-12px"}}  
          whileInView={{ opacity: 1, translateY: "0px" }} 
          transition={{duration: .7, delay: 1.5}} 
          className="mt-[50px] md:mt-[70px] text-xs md:text-sm text-gray-400 max-w-2xl mx-auto select-none"
        >
          <motion.span 
            className="text-indigo-300 font-medium relative inline-block"
            whileHover={{ color: "#a78bfa" }}
          >
            I coded the designs created in Adobe XD
            <motion.div 
              className="absolute -bottom-1 left-0 right-0 h-[1px] bg-indigo-400/30"
              initial={{ scaleX: 0 }}
              whileHover={{ scaleX: 1 }}
              transition={{ duration: 0.3 }}
            />
          </motion.span> for the <br /> 
          Dashboard and Homepage at SVAYP Company <br /> 
          using ReactJS and various packages. I collaborated with my team to add <br /> 
          new features and improvements by exchanging ideas.
        </motion.div>
      </div>
      
      {/* Phone images with animations - desktop only */}
      <motion.div 
        className="absolute top-0 left-0 xl:block hidden"
        initial={{ opacity: 0, translateX: "0px"}}  
        viewport={{ margin: "-320px" }}  
        whileInView={{ opacity: 1, translateX: "120px" }} 
        transition={{duration: .7}}
        style={{
          transform: `translateX(120px) translateY(${scrollY * 0.03}px)`,
          transition: 'transform 0.1s ease-out'
        }}
      >
        <motion.div className="relative">
          <motion.img  
            className='w-[20rem] drop-shadow-2xl' 
            src={SvaypRightMobilePhone} 
            alt="Svayp Mobile App" 
            whileHover={{ 
              y: -10,
              filter: "drop-shadow(0 10px 15px rgba(0, 0, 0, 0.3))",
              transition: { duration: 0.5, yoyo: Infinity, ease: "easeInOut" }
            }}
          />
          <motion.div 
            className="absolute -inset-4 -z-10 bg-gradient-to-r from-purple-600/10 to-indigo-600/5 rounded-[40px] blur-md opacity-0 hover:opacity-100 transition duration-700"
          />
          
          {/* Screen glow effect */}
          <motion.div 
            className="absolute inset-0 bg-purple-500/5 rounded-[30px] blur-sm opacity-0"
            animate={{
              opacity: [0, 0.5, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
          
          {/* Animated dots */}
          <motion.div 
            className="absolute top-1/4 right-0 w-2 h-2 bg-purple-500 rounded-full"
            animate={{
              opacity: [0, 1, 0],
              scale: [0.8, 1.2, 0.8],
              x: [0, 10, 0],
              boxShadow: [
                "0 0 0px rgba(168, 85, 247, 0)",
                "0 0 4px rgba(168, 85, 247, 0.5)",
                "0 0 0px rgba(168, 85, 247, 0)"
              ]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatType: "loop",
              delay: 0.5
            }}
          />
          <motion.div 
            className="absolute top-1/2 right-4 w-1.5 h-1.5 bg-indigo-500 rounded-full"
            animate={{
              opacity: [0, 1, 0],
              scale: [0.8, 1.2, 0.8],
              x: [0, -5, 0],
              boxShadow: [
                "0 0 0px rgba(139, 92, 246, 0)",
                "0 0 4px rgba(139, 92, 246, 0.5)",
                "0 0 0px rgba(139, 92, 246, 0)"
              ]
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              repeatType: "loop",
              delay: 1
            }}
          />
        </motion.div>
      </motion.div>
      
      <motion.div 
        className="absolute top-0 right-0 xl:block hidden"
        initial={{ opacity: 0, translateX: "0px"}}  
        viewport={{ margin: "-320px" }}  
        whileInView={{ opacity: 1, translateX: "-100px" }} 
        transition={{duration: .7}}
        style={{
          transform: `translateX(-100px) translateY(${scrollY * 0.02}px)`,
          transition: 'transform 0.1s ease-out'
        }}
      >
        <motion.div className="relative">
          <motion.img  
            className='w-[20rem] drop-shadow-2xl' 
            src={SvaypLeftMobilePhone} 
            alt="Svayp Mobile App" 
            whileHover={{ 
              y: -10,
              filter: "drop-shadow(0 10px 15px rgba(0, 0, 0, 0.3))",
              transition: { duration: 0.5, yoyo: Infinity, ease: "easeInOut" }
            }}
          />
          <motion.div 
            className="absolute -inset-4 -z-10 bg-gradient-to-r from-indigo-600/5 to-purple-600/10 rounded-[40px] blur-md opacity-0 hover:opacity-100 transition duration-700"
          />
          
          {/* Screen glow effect */}
          <motion.div 
            className="absolute inset-0 bg-indigo-500/5 rounded-[30px] blur-sm opacity-0"
            animate={{
              opacity: [0, 0.5, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              repeatType: "reverse",
              delay: 2
            }}
          />
          
          {/* Animated dots */}
          <motion.div 
            className="absolute top-1/3 left-0 w-2 h-2 bg-indigo-500 rounded-full"
            animate={{
              opacity: [0, 1, 0],
              scale: [0.8, 1.2, 0.8],
              x: [0, -10, 0],
              boxShadow: [
                "0 0 0px rgba(139, 92, 246, 0)",
                "0 0 4px rgba(139, 92, 246, 0.5)",
                "0 0 0px rgba(139, 92, 246, 0)"
              ]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatType: "loop",
              delay: 1.5
            }}
          />
          <motion.div 
            className="absolute top-2/3 left-4 w-1.5 h-1.5 bg-purple-500 rounded-full"
            animate={{
              opacity: [0, 1, 0],
              scale: [0.8, 1.2, 0.8],
              x: [0, 5, 0],
              boxShadow: [
                "0 0 0px rgba(168, 85, 247, 0)",
                "0 0 4px rgba(168, 85, 247, 0.5)",
                "0 0 0px rgba(168, 85, 247, 0)"
              ]
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              repeatType: "loop",
              delay: 0.8
            }}
          />
        </motion.div>
      </motion.div>
      
      {/* Mobile phone images - only visible on smaller screens */}
      <div className="flex justify-center mt-10 md:mt-12 xl:hidden">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.8 }}
          className="flex gap-4 md:gap-8"
        >
          <motion.div 
            className="relative"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300, damping: 10 }}
          >
            <motion.img 
              className='w-[7rem] md:w-[12rem] drop-shadow-xl'
              src={SvaypRightMobilePhone} 
              alt="Svayp Mobile App" 
              whileHover={{ y: -5 }}
              whileTap={{ scale: 0.98 }}
            />
            <motion.div 
              className="absolute -inset-2 -z-10 bg-gradient-to-r from-purple-600/10 to-indigo-600/5 rounded-[20px] blur-md opacity-0 hover:opacity-100 transition duration-700"
            />
            
            {/* Screen glow effect */}
            <motion.div 
              className="absolute inset-0 bg-purple-500/5 rounded-[15px] blur-sm opacity-0"
              animate={{
                opacity: [0, 0.5, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                repeatType: "reverse",
              }}
            />
            
            {/* Small animated dot */}
            <motion.div 
              className="absolute top-1/4 right-0 w-1 h-1 md:w-1.5 md:h-1.5 bg-purple-500 rounded-full"
              animate={{
                opacity: [0, 1, 0],
                scale: [0.8, 1.2, 0.8],
                boxShadow: [
                  "0 0 0px rgba(168, 85, 247, 0)",
                  "0 0 3px rgba(168, 85, 247, 0.5)",
                  "0 0 0px rgba(168, 85, 247, 0)"
                ]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "loop",
                delay: 0.5
              }}
            />
          </motion.div>
          
          <motion.div 
            className="relative"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300, damping: 10 }}
          >
            <motion.img 
              className='w-[7rem] md:w-[12rem] drop-shadow-xl'
              src={SvaypLeftMobilePhone} 
              alt="Svayp Mobile App" 
              whileHover={{ y: -5 }}
              whileTap={{ scale: 0.98 }}
            />
            <motion.div 
              className="absolute -inset-2 -z-10 bg-gradient-to-r from-indigo-600/5 to-purple-600/10 rounded-[20px] blur-md opacity-0 hover:opacity-100 transition duration-700"
            />
            
            {/* Screen glow effect */}
            <motion.div 
              className="absolute inset-0 bg-indigo-500/5 rounded-[15px] blur-sm opacity-0"
              animate={{
                opacity: [0, 0.5, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                repeatType: "reverse",
                delay: 2
              }}
            />
            
            {/* Small animated dot */}
            <motion.div 
              className="absolute top-2/3 left-0 w-1 h-1 md:w-1.5 md:h-1.5 bg-indigo-500 rounded-full"
              animate={{
                opacity: [0, 1, 0],
                scale: [0.8, 1.2, 0.8],
                boxShadow: [
                  "0 0 0px rgba(139, 92, 246, 0)",
                  "0 0 3px rgba(139, 92, 246, 0.5)",
                  "0 0 0px rgba(139, 92, 246, 0)"
                ]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "loop",
                delay: 1
              }}
            />
          </motion.div>
        </motion.div> 
      </div>
    </motion.div>
  )
}

export default Svayp